import { useEffect, useState } from "react";
import { Button, EmptyState, IconButton, Input, SegmentedControl, Toast } from "../components";
import { Icon, useLucideIcons } from "../components/Icon";
import { api } from "../lib/api";
import { useIsMobile } from "../lib/hooks";
import type { ScheduleEvent, Student } from "../lib/types";
import type { ScheduleFormData } from "./ScheduleDialog";
import { Dashboard } from "./Dashboard";
import { LoginPage } from "./LoginPage";
import { People } from "./People";
import { Reports } from "./Reports";
import { Schedule } from "./Schedule";
import { ScheduleDialog } from "./ScheduleDialog";
import { Shell, type AdminPage } from "./Shell";

const META: Record<AdminPage, { title: string; subtitle: string }> = {
  dashboard: { title: "概览", subtitle: "WELL-E · 2026年6月" },
  schedule: { title: "排课日历", subtitle: "" },
  people: { title: "人员管理", subtitle: "在读学员 156 人" },
  leave: { title: "请假审批", subtitle: "3 条待处理" },
  reports: { title: "课时报表", subtitle: "按学员统计 · 2026年6月" },
  settings: { title: "设置", subtitle: "课时规则与账户" },
};

const RANGE_LABELS: Record<string, string> = { day: "日", week: "周", month: "月" };

// ── Date helpers ──────────────────────────────────────────────────────────

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getMondayOf(d: Date): Date {
  const day = d.getDay();
  const m = new Date(d);
  m.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  m.setHours(0, 0, 0, 0);
  return m;
}

function shiftDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

// ── Persistent locally-created events (localStorage) ─────────────────────

const ADDED_EVENTS_KEY = "jishi_added_events";

function loadAddedEvents(): ScheduleEvent[] {
  try {
    const s = localStorage.getItem(ADDED_EVENTS_KEY);
    return s ? (JSON.parse(s) as ScheduleEvent[]) : [];
  } catch { return []; }
}

function saveAddedEvents(evs: ScheduleEvent[]) {
  localStorage.setItem(ADDED_EVENTS_KEY, JSON.stringify(evs));
}

function mergeAddedEvents(base: ScheduleEvent[], added: ScheduleEvent[]): ScheduleEvent[] {
  if (!added.length) return base;
  const keys = new Set(base.map(e => `${e.student_id}|${e.date}|${e.slot}`));
  return [...base, ...added.filter(e => !keys.has(`${e.student_id}|${e.date}|${e.slot}`))];
}

// ── Persistent "delete future" rules (localStorage) ──────────────────────

const DELETED_FUTURES_KEY = "jishi_deleted_futures";

interface DeletedFutureRule { studentIds: number[]; fromDate: string }

function loadDeletedFutures(): DeletedFutureRule[] {
  try {
    const s = localStorage.getItem(DELETED_FUTURES_KEY);
    return s ? (JSON.parse(s) as DeletedFutureRule[]) : [];
  } catch { return []; }
}

function saveDeletedFutures(rules: DeletedFutureRule[]) {
  localStorage.setItem(DELETED_FUTURES_KEY, JSON.stringify(rules));
}

function applyDeletedFutures(evs: ScheduleEvent[], rules?: DeletedFutureRule[]): ScheduleEvent[] {
  const r = rules ?? loadDeletedFutures();
  if (!r.length) return evs;
  return evs.filter((e) => {
    if (!e.date) return true;
    return !r.some((rule) => {
      if (e.date! < rule.fromDate) return false;
      const eIds = e.studentIds ?? (e.student_id != null ? [e.student_id] : []);
      return rule.studentIds.some((sid) => eIds.includes(sid));
    });
  });
}

// ── Expand seed/recurring events into dated instances ─────────────────────

let seedInstId = 10_000;     // seed instance IDs
let localIdCounter = 100_000; // locally-created events

function generateSeriesId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function todayStr(): string { return toDateStr(new Date()); }

function deduplicateEvents(evs: ScheduleEvent[]): ScheduleEvent[] {
  const seen = new Set<string>();
  return evs.filter(ev => {
    const key = `${ev.student_id}|${ev.date ?? ev.day}|${ev.slot}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function expandSeedEvents(seedEvents: ScheduleEvent[]): ScheduleEvent[] {
  const today = todayStr();
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to   = new Date(now.getFullYear(), now.getMonth() + 3, 0);
  const expanded: ScheduleEvent[] = [];

  for (const ev of seedEvents) {
    // Events with a specific date are concrete DB records — keep as-is (preserve real id).
    if (ev.date) {
      expanded.push({
        ...ev,
        status: ev.date < today ? "taught" : ev.status,
      });
      continue;
    }

    // No date → recurring day-of-week pattern; expand across the display window.
    let d = new Date(from);
    while ((d.getDay() + 6) % 7 !== ev.day) d.setDate(d.getDate() + 1);
    while (d <= to) {
      const date = toDateStr(d);
      expanded.push({
        ...ev,
        id: seedInstId++,
        date,
        status: date < today ? "taught" : ev.status,
      });
      d.setDate(d.getDate() + 7);
    }
  }
  return expanded;
}

// ── Generate all dated instances for a form submission ────────────────────

function generateInstances(data: ScheduleFormData): Array<{ date: string; dow: number }> {
  const start = new Date(data.startDate + "T00:00:00");
  start.setHours(0, 0, 0, 0);

  if (data.repeat === "none") {
    return [{ date: data.startDate, dow: (start.getDay() + 6) % 7 }];
  }

  const end = data.endType === "date"
    ? (() => { const e = new Date(data.endDate + "T23:59:59"); return e; })()
    : new Date(start.getFullYear() + 1, start.getMonth(), start.getDate());

  const maxCount = data.endType === "count" ? data.endCount : 500;
  const results: Array<{ date: string; dow: number }> = [];

  if (data.repeat === "monthly") {
    let d = new Date(start);
    while (d <= end && results.length < maxCount) {
      results.push({ date: toDateStr(d), dow: (d.getDay() + 6) % 7 });
      d = new Date(d.getFullYear(), d.getMonth() + 1, d.getDate());
    }
    return results;
  }

  // weekly or biweekly — generate one entry per (week × repeatDay)
  const interval = data.repeat === "biweekly" ? 14 : 7;
  const dows = (data.repeatDays.length > 0 ? [...data.repeatDays] : [(start.getDay() + 6) % 7])
    .sort((a, b) => a - b);

  let weekMon = getMondayOf(start);
  while (weekMon <= end && results.length < maxCount) {
    for (const dow of dows) {
      const d = shiftDays(weekMon, dow); // dow 0=Mon offset from Monday
      if (d >= start && d <= end && results.length < maxCount) {
        results.push({ date: toDateStr(d), dow });
      }
    }
    weekMon = shiftDays(weekMon, interval);
  }
  return results;
}

// ── Convert ScheduleFormData → ScheduleEvent list ─────────────────────────

function formDataToEvents(data: ScheduleFormData, students: Student[]): ScheduleEvent[] {
  const selected = students.filter((s) => data.studentIds.includes(s.id));
  if (selected.length === 0) return [];

  const combinedName =
    selected.length === 1 ? selected[0].name
    : selected.length === 2 ? `${selected[0].name}、${selected[1].name}`
    : `${selected[0].name} 等${selected.length}人`;

  const [sh, sm] = data.startTime.split(":").map(Number);
  const [eh, em] = data.endTime.split(":").map(Number);
  const durationMin = eh * 60 + em - (sh * 60 + sm);
  const hours = Math.max(0.5, Math.round(durationMin / 30) * 0.5);

  const instances = generateInstances(data);
  const sId = instances.length > 1 ? generateSeriesId() : undefined;
  const rc = data.repeat !== "none" ? {
    repeat: data.repeat,
    repeatDays: data.repeatDays,
    endType: data.endType,
    endDate: data.endDate,
    endCount: data.endCount,
  } : undefined;
  const todayDate = todayStr();

  return instances.map(({ date, dow }) => ({
    id: ++localIdCounter,
    student_id: selected[0].id,
    studentIds: selected.map((s) => s.id),
    day: dow,
    slot: data.startTime,
    end_time: data.endTime,
    hours,
    name: combinedName,
    subject: `${data.subject} · ${data.type}`,
    status: date < todayDate ? "taught" as const : "upcoming" as const,
    date,
    seriesId: sId,
    repeatConfig: rc,
  }));
}

// ── Component ─────────────────────────────────────────────────────────────

export function AdminApp() {
  const [token, setToken] = useState(() => localStorage.getItem("jishi_token") ?? "");

  if (!token) {
    return (
      <LoginPage
        onLogin={(t) => {
          localStorage.setItem("jishi_token", t);
          setToken(t);
        }}
      />
    );
  }

  return <AdminAppInner onLogout={() => { localStorage.removeItem("jishi_token"); setToken(""); }} />;
}

function AdminAppInner({ onLogout }: { onLogout: () => void }) {
  const [page, setPage] = useState<AdminPage>("schedule");
  const [range, setRange] = useState<"day" | "week" | "month">("week");

  // Events live here so Schedule re-renders immediately on add/delete
  const [events, setEvents] = useState<ScheduleEvent[]>([]);

  // Dialog state — one dialog handles new / click-to-add / edit
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleStudent, setScheduleStudent] = useState<Student | undefined>();
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>();
  const [scheduleTime, setScheduleTime] = useState<string | undefined>();
  const [editEvent, setEditEvent] = useState<ScheduleEvent | undefined>();

  const [students, setStudents] = useState<Student[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [peopleAddOpen, setPeopleAddOpen] = useState(false);
  const [peopleSearch, setPeopleSearch] = useState("");
  useLucideIcons([page, scheduleOpen]);

  // Auto-dismiss toast after 3 s
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    // DB returns both concrete dated events (date IS NOT NULL) and recurring
    // day-of-week patterns (date IS NULL). expandSeedEvents passes dated events
    // through unchanged and expands recurring ones across the display window.
    api.schedule().then((d) => {
      const expanded = deduplicateEvents(expandSeedEvents(d.events));
      setEvents(applyDeletedFutures(expanded));
    }).catch(() => {});
    api.students().then(setStudents).catch(() => {});
    api
      .leaves()
      .then((ls) => setPendingLeaves(ls.filter((l) => l.status === "pending").length))
      .catch(() => {});
  }, []);

  // ── Dialog openers ──────────────────────────────────────────────────

  const openNew = () => {
    setEditEvent(undefined);
    setScheduleStudent(undefined);
    setScheduleDate(undefined);
    setScheduleTime(undefined);
    setScheduleOpen(true);
  };

  const openForStudent = (student: Student) => {
    setEditEvent(undefined);
    setScheduleStudent(student);
    setScheduleDate(undefined);
    setScheduleTime(undefined);
    setScheduleOpen(true);
  };

  const openAtSlot = (dow: number, time: string, date: Date) => {
    setEditEvent(undefined);
    setScheduleStudent(undefined);
    setScheduleDate(date);
    setScheduleTime(time);
    setScheduleOpen(true);
  };

  const openEdit = (ev: ScheduleEvent) => {
    setEditEvent(ev);
    setScheduleStudent(undefined);
    setScheduleDate(undefined);
    setScheduleTime(undefined);
    setScheduleOpen(true);
  };

  const closeDialog = () => {
    setScheduleOpen(false);
    setEditEvent(undefined);
  };

  // ── Save handler ────────────────────────────────────────────────────

  const handleSave = (data: ScheduleFormData) => {
    closeDialog();

    if (editEvent) {
      // "此后所有" — replace every future instance of the series starting from this event
      if (data.saveScope === "future" && (editEvent.seriesId || editEvent.repeatConfig)) {
        const cutoff = editEvent.date ?? "";

        // 1. Find all future events of this series in local state
        const futureEvs = events.filter((e) => {
          if ((e.date ?? "") < cutoff) return false;
          if (editEvent.seriesId && e.seriesId === editEvent.seriesId) return true;
          // Fall back: same student_id + slot + end_time (for events without seriesId)
          return !editEvent.seriesId &&
            e.student_id === editEvent.student_id &&
            e.slot === editEvent.slot &&
            e.end_time === editEvent.end_time;
        });

        // 2. Delete from DB — bulk delete by seriesId if available, else one by one
        if (editEvent.seriesId) {
          api.deleteSeriesFuture(editEvent.seriesId, cutoff).catch(() => {});
        } else {
          futureEvs.forEach((e) => {
            if (e.id < 10_000) api.deleteSchedule(e.id).catch(() => {});
          });
        }

        // 3. Remove from local state
        const futureIds = new Set(futureEvs.map((e) => e.id));
        setEvents((prev) => prev.filter((e) => !futureIds.has(e.id)));

        // 4. Create replacement events starting from cutoff date
        const newEvs = formDataToEvents(
          { ...data, startDate: editEvent.date ?? data.startDate },
          students
        );
        setEvents((prev) => [...prev, ...newEvs]);

        // 5. Persist new events to DB
        newEvs.forEach((ev) => {
          api.createSchedule({
            student_id: ev.student_id,
            day: ev.day,
            slot: ev.slot,
            end_time: ev.end_time,
            hours: ev.hours,
            subject: ev.subject,
            date: ev.date,
            student_ids: ev.studentIds,
            repeat_config: ev.repeatConfig,
            series_id: ev.seriesId,
          }).then(({ id }) => {
            setEvents((prev) => prev.map((e) =>
              e.student_id === ev.student_id && e.date === ev.date && e.slot === ev.slot
                ? { ...e, id }
                : e
            ));
          }).catch(() => {});
        });

        setToast("此后排课已更新");
        return;
      }

      // Edit: update the single event in local state
      const editSelected = students.filter((s) => data.studentIds.includes(s.id));
      const editName = editSelected.length === 0 ? editEvent.name
        : editSelected.length === 1 ? editSelected[0].name
        : editSelected.length === 2 ? `${editSelected[0].name}、${editSelected[1].name}`
        : `${editSelected[0].name} 等${editSelected.length}人`;
      // Derive aggregate status: absent wins > leave > taught > upcoming
      const statVals = Object.values(data.studentStatuses ?? {});
      const aggStatus: ScheduleEvent["status"] =
        statVals.length === 0 ? editEvent.status
        : statVals.some((s) => s === "absent") ? "absent"
        : statVals.some((s) => s === "leave") ? "leave"
        : statVals.every((s) => s === "taught") ? "taught"
        : "upcoming";

      const updated: ScheduleEvent = {
        ...editEvent,           // keeps editEvent.date intact
        student_id: data.studentIds[0] ?? editEvent.student_id,
        studentIds: data.studentIds,
        studentStatuses: data.studentStatuses,
        slot: data.startTime,
        end_time: data.endTime,
        name: editName,
        subject: `${data.subject} · ${data.type}`,
        status: aggStatus,
      };
      setEvents((prev) => prev.map((e) => (e.id === editEvent.id ? updated : e)));
      setToast("排课已更新");
    } else {
      // New: convert form data → events and append (deduplicate against existing)
      const newEvs = formDataToEvents(data, students);

      // Clear any "delete future" rules for these students
      const addedIds = new Set(data.studentIds);
      const updatedRules = loadDeletedFutures().filter(
        (r) => !r.studentIds.some((sid) => addedIds.has(sid))
      );
      saveDeletedFutures(updatedRules);

      // Add to local state immediately (optimistic), then persist each event to DB.
      // API returns the real DB id; replace the temp id so future deletes hit the right row.
      setEvents((prev) => {
        const existingKeys = new Set(prev.map(e => `${e.student_id}|${e.date}|${e.slot}`));
        const unique = newEvs.filter(e => !existingKeys.has(`${e.student_id}|${e.date}|${e.slot}`));
        return [...prev, ...unique];
      });

      newEvs.forEach((ev) => {
        api.createSchedule({
          student_id: ev.student_id,
          day: ev.day,
          slot: ev.slot,
          end_time: ev.end_time,
          hours: ev.hours,
          subject: ev.subject,
          date: ev.date,
          student_ids: ev.studentIds,
          repeat_config: ev.repeatConfig,
          series_id: ev.seriesId,
        }).then(({ id }) => {
          // Swap temp id → real DB id so delete/edit can reference the correct row
          setEvents((prev) => prev.map((e) =>
            e.student_id === ev.student_id && e.date === ev.date && e.slot === ev.slot
              ? { ...e, id }
              : e
          ));
        }).catch(() => {});
      });

      const student = students.find((s) => s.id === data.studentIds[0]);
      const repeatText =
        data.repeat === "weekly"
          ? "每周重复"
          : data.repeat === "biweekly"
          ? "每两周重复"
          : data.repeat === "monthly"
          ? "每月重复"
          : "单次";
      setToast(
        student
          ? `已为 ${student.name} 添加 ${newEvs.length} 节课 · ${repeatText}`
          : "排课已保存"
      );
    }
  };

  // ── Delete handler ──────────────────────────────────────────────────

  const handleDelete = (eventId: number) => {
    closeDialog();
    // Real DB id = assigned by autoincrement (below the synthetic seedInstId/localIdCounter floor).
    // Synthetic ids (>=10000) are expanded recurring-pattern instances — no DB row to delete.
    if (eventId < 10_000) {
      api.deleteSchedule(eventId).catch(() => {});
    }
    setEvents((prev) => prev.filter((e) => e.id !== eventId));
    setToast("课程已删除");
  };

  const handleDeleteFuture = (sIds: number[], fromDate: string) => {
    closeDialog();

    // Delete concrete DB rows for affected dated events
    events.forEach((e) => {
      if (!e.date || e.date < fromDate || e.id >= 10_000) return;
      const eIds = e.studentIds ?? (e.student_id != null ? [e.student_id] : []);
      if (sIds.some((sid) => eIds.includes(sid))) {
        api.deleteSchedule(e.id).catch(() => {});
      }
    });

    // Keep localStorage rule to filter any expanded recurring-pattern instances
    const rules = loadDeletedFutures();
    rules.push({ studentIds: sIds, fromDate });
    saveDeletedFutures(rules);
    setEvents((prev) => applyDeletedFutures(prev, rules));
    const names = students.filter((s) => sIds.includes(s.id)).map((s) => s.name).join("、");
    setToast(`已删除 ${names} 在 ${fromDate} 之后的所有课程`);
  };

  // ── UI ──────────────────────────────────────────────────────────────

  const isMobile = useIsMobile();
  const now = new Date();
  const scheduleSubtitle = `按${RANGE_LABELS[range]}显示 · ${now.getFullYear()}年${now.getMonth() + 1}月`;
  const dashboardSubtitle = `${now.getFullYear()}年${now.getMonth() + 1}月`;
  const activeCount = students.filter((s) => s.status === "upcoming").length;
  const peopleSubtitle = students.length === 0 ? "" : `在读学员 ${activeCount} 人`;

  const actions = (() => {
    if (page === "schedule") {
      return (
        <>
          <SegmentedControl
            value={range}
            onChange={(v) => setRange(v as "day" | "week" | "month")}
            options={[
              { value: "day", label: "日" },
              { value: "week", label: "周" },
              { value: "month", label: "月" },
            ]}
          />
          {isMobile ? (
            <IconButton label="新建排课" tone="brand" onClick={openNew}>
              <Icon name="plus" size={20} />
            </IconButton>
          ) : (
            <Button variant="primary" iconLeft={<Icon name="plus" />} onClick={openNew}>
              新建排课
            </Button>
          )}
        </>
      );
    }
    if (page === "people") {
      return (
        <>
          {!isMobile && (
            <div style={{ width: 200 }}>
              <Input
                icon={<Icon name="search" />}
                placeholder="按姓名 / 年级搜索"
                value={peopleSearch}
                onChange={(e) => setPeopleSearch(e.target.value)}
              />
            </div>
          )}
          {isMobile ? (
            <IconButton label="新增学员" tone="brand" onClick={() => setPeopleAddOpen(true)}>
              <Icon name="user-plus" size={20} />
            </IconButton>
          ) : (
            <Button variant="primary" iconLeft={<Icon name="user-plus" />} onClick={() => setPeopleAddOpen(true)}>
              新增学员
            </Button>
          )}
        </>
      );
    }
    if (page === "reports") {
      return isMobile ? null : (
        <Button variant="accent" iconLeft={<Icon name="download" />} onClick={() => setToast("报表已开始导出")}>
          导出报表
        </Button>
      );
    }
    if (page === "dashboard") {
      return isMobile ? null : (
        <Button variant="secondary" iconLeft={<Icon name="calendar-days" />} onClick={() => setPage("schedule")}>
          查看排课
        </Button>
      );
    }
    return null;
  })();

  const screen = (() => {
    switch (page) {
      case "dashboard":
        return <Dashboard events={events} students={students} pendingLeaves={pendingLeaves} />;
      case "schedule":
        return (
          <Schedule
            range={range}
            events={events}
            students={students}
            onAddSlot={openAtSlot}
            onEditEvent={openEdit}
          />
        );
      case "people":
        return (
          <People
            events={events}
            onSchedule={openForStudent}
            searchTerm={peopleSearch}
            addOpen={peopleAddOpen}
            onAddClose={() => setPeopleAddOpen(false)}
            onStudentSaved={(s) =>
              setStudents((prev) =>
                prev.some((x) => x.id === s.id)
                  ? prev.map((x) => (x.id === s.id ? s : x))
                  : [...prev, s]
              )
            }
            onStudentDeleted={(id) =>
              setStudents((prev) => prev.filter((s) => s.id !== id))
            }
          />
        );
      case "reports":
        return <Reports events={events} students={students} />;
      default:
        return (
          <EmptyState
            icon={<Icon name="hammer" size={26} />}
            title={META[page].title}
            description="该界面待开发。"
          />
        );
    }
  })();

  return (
    <>
      <Shell
        page={page}
        onNav={setPage}
        title={META[page].title}
        subtitle={
          page === "schedule" ? scheduleSubtitle
          : page === "dashboard" ? dashboardSubtitle
          : page === "people" ? peopleSubtitle
          : META[page].subtitle
        }
        actions={actions}
        pendingLeaves={pendingLeaves}
        onLogout={onLogout}
      >
        {screen}
      </Shell>

      <ScheduleDialog
        open={scheduleOpen}
        students={students}
        editEvent={editEvent}
        defaultStudentId={scheduleStudent?.id}
        defaultDate={scheduleDate}
        defaultTime={scheduleTime}
        onClose={closeDialog}
        onSave={handleSave}
        onDelete={handleDelete}
        onDeleteFuture={handleDeleteFuture}
      />

      {toast && (
        <div style={{ position: "fixed", right: 24, bottom: 24, zIndex: 2000 }}>
          <Toast tone="success" title="操作成功" onClose={() => setToast(null)}>
            {toast}
          </Toast>
        </div>
      )}
    </>
  );
}
